/**
 * Composant Iframe
 * Gère les iframes auto-height
 */

import { logger } from '../../core/base.js';

export function initIframes() {
    const syncIframeHeight = iframe => {
        // contentDocument is null across origins; reading contentWindow.document throws.
        const doc = iframe.contentDocument;

        if (!doc) {
            return;
        }

        // The boxes, not scrollHeight: a root element's scrollHeight never drops below the
        // frame's own height, so a frame sized once on an unstyled document (fonts and
        // stylesheet still loading, the table taller than it will be) kept that height for
        // good — 130 px of blank under the calendar, measured on layering the 2026-09-07.
        // The root's box and the body's bottom edge follow the content both ways.
        const html = doc.documentElement;
        const nextHeight = Math.ceil(Math.max(
            html ? html.getBoundingClientRect().height : 0,
            doc.body ? doc.body.getBoundingClientRect().bottom : 0
        ));

        if (nextHeight > 0) {
            // style.height, et NON l'attribut `height`. Un attribut de presentation
            // perd contre n'importe quelle regle d'auteur : une feuille de theme qui
            // declare `.auto-height { height: auto }` ramenait l'iframe aux 150 px du
            // defaut HTML et annulait la mesure qu'on vient de faire. Mesure ecrite en
            // style en ligne, elle ne depend plus de ce qu'aucune feuille ne dit.
            iframe.style.height = `${nextHeight}px`;
        }
    };

    const watch = iframe => {
        let observer;

        // The document keeps reflowing after `load`: a webfont swap re-wraps the
        // calendar's navigation pills and grows the body by a line, past the last
        // timed measure. Measured on layering (2026-09-07): the last week of the
        // month sat clipped under the frame. Same-origin, so the root of the child
        // document can be observed from here; the observer is re-attached on every
        // load because an in-frame navigation replaces the document.
        const observe = () => {
            observer?.disconnect();
            const root = iframe.contentDocument?.documentElement;

            if (!root || typeof ResizeObserver === 'undefined') {
                return;
            }

            observer = new ResizeObserver(() => syncIframeHeight(iframe));
            observer.observe(root);
        };

        const syncSoon = () => {
            syncIframeHeight(iframe);
            requestAnimationFrame(() => syncIframeHeight(iframe));
            window.setTimeout(() => syncIframeHeight(iframe), 150);
            window.setTimeout(() => syncIframeHeight(iframe), 500);
            observe();
        };

        iframe.addEventListener('load', syncSoon);

        // L'iframe a pu finir de charger AVANT que ce script ne tourne : elle demarre
        // son chargement des l'analyse du document, et une page de meme origine rend
        // souvent plus vite que le DOMContentLoaded du parent. L'ecouteur `load` seul
        // ne se declenchait alors jamais, et le calendrier restait a la taille par
        // defaut d'une iframe — 300x150 — sans qu'aucune erreur ne le signale.
        if (iframe.contentDocument?.readyState === 'complete') {
            syncSoon();
        }
    };

    document.querySelectorAll('iframe.auto-height').forEach(watch);

    logger.debug('Iframes initialized');
}

document.addEventListener('DOMContentLoaded', initIframes);
